import { useEffect, useState } from 'react';

import { getMentionActivity } from '../api/client.js';

function dotColor(action) {
  switch (action) {
    case 'command_center_flagged':
      return '#FFAE1E';
    case 'command_center_unflagged':
      return '#718096';
    case 'status_change':
      return 'var(--color-primary)';
    case 'severity_change':
      return 'var(--color-critical)';
    case 'notes_updated':
      return 'var(--color-accent)';
    case 'created':
      return 'var(--color-low)';
    default:
      return '#A0AEC0';
  }
}

function actionText(entry) {
  switch (entry.action) {
    case 'status_change':
      return (<>changed Status to <strong>{entry.new_value || '—'}</strong></>);
    case 'severity_change':
      return (<>changed Severity to <strong>{entry.new_value || '—'}</strong></>);
    case 'notes_updated':
      return 'updated Notes';
    case 'command_center_flagged':
      return 'submitted to Command Center';
    case 'command_center_unflagged':
      return 'removed Command Center flag';
    case 'created':
      return (<>Mention received from <strong>{entry.new_value || '—'}</strong></>);
    default:
      return `${entry.field_name} changed`;
  }
}

function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (seconds < 45) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
  return `${Math.round(days / 365)} year(s) ago`;
}

function absoluteTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function Skeleton() {
  return (
    <ul className="activity-log">
      {[0, 1, 2].map((i) => (
        <li key={i} className="activity-log__entry">
          <span className="activity-log__dot activity-log__dot--skeleton" aria-hidden="true" />
          <div className="activity-log__body">
            <div className="skeleton-cell" style={{ width: '75%', height: 12 }} />
            <div className="skeleton-cell" style={{ width: '40%', height: 10, marginTop: 6 }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

export default function MentionActivityLog({ mentionId, refreshKey = 0 }) {
  const [entries, setEntries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!mentionId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getMentionActivity(mentionId)
      .then((data) => !cancelled && setEntries(data.activity || []))
      .catch((e) => !cancelled && setError(e.message || 'Failed to load activity'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [mentionId, refreshKey]);

  if (loading) return <Skeleton />;
  if (error) return <div className="form-error">{error}</div>;
  if (!entries || entries.length === 0) {
    return <div className="activity-log__empty">No activity yet</div>;
  }

  return (
    <ul className="activity-log">
      {entries.map((entry) => (
        <li key={entry.id} className="activity-log__entry">
          <span className="activity-log__dot" style={{ background: dotColor(entry.action) }} aria-hidden="true" />
          <div className="activity-log__body">
            <div className="activity-log__text">
              <span className="activity-log__actor">{entry.user_display}</span>{' '}
              {actionText(entry)}
            </div>
            <div className="activity-log__time" title={absoluteTime(entry.created_at)}>
              {relativeTime(entry.created_at)}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}
